import { z } from 'zod';

/**
 * Stable, documented error codes. Operators integrate against these strings,
 * so they are part of the public contract and must never be renamed silently.
 */
export const ProviderErrorCode = z.enum([
  // Authentication / transport
  'INVALID_SIGNATURE',
  'INVALID_TIMESTAMP',
  'IP_NOT_ALLOWED',
  'OPERATOR_NOT_FOUND',
  'OPERATOR_DISABLED',
  'VALIDATION_ERROR',
  'RATE_LIMITED',

  // Catalogue
  'GAME_NOT_FOUND',
  'GAME_DISABLED',
  'CURRENCY_NOT_SUPPORTED',

  // Session
  'INVALID_TOKEN',
  'TOKEN_EXPIRED',
  'TOKEN_ALREADY_USED',
  'SESSION_EXPIRED',
  'SESSION_CLOSED',

  // Betting
  'INVALID_BET',
  'BET_BELOW_MIN',
  'BET_ABOVE_MAX',
  'INSUFFICIENT_FUNDS',
  'PLAYER_BLOCKED',
  'PLAYER_NOT_FOUND',

  // Round lifecycle
  'ROUND_IN_PROGRESS',
  'ROUND_NOT_FOUND',
  'ROUND_ALREADY_CLOSED',
  'ROUND_NOT_CANCELLABLE',

  // Wallet integration
  'WALLET_UNAVAILABLE',
  'WALLET_REJECTED',

  'GAME_UNAVAILABLE',
  'INTERNAL_ERROR',
]);

export type ProviderErrorCode = z.infer<typeof ProviderErrorCode>;

export const ErrorResponse = z.object({
  status: z.literal('ERROR'),
  code: ProviderErrorCode,
  message: z.string(),
  /** Echoed back so operators can correlate with their own logs. */
  requestId: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponse>;

const HTTP_STATUS: Record<ProviderErrorCode, number> = {
  INVALID_SIGNATURE: 401,
  INVALID_TIMESTAMP: 401,
  IP_NOT_ALLOWED: 403,
  OPERATOR_NOT_FOUND: 401,
  OPERATOR_DISABLED: 403,
  VALIDATION_ERROR: 400,
  RATE_LIMITED: 429,

  GAME_NOT_FOUND: 404,
  GAME_DISABLED: 403,
  CURRENCY_NOT_SUPPORTED: 400,

  INVALID_TOKEN: 401,
  TOKEN_EXPIRED: 401,
  TOKEN_ALREADY_USED: 401,
  SESSION_EXPIRED: 401,
  SESSION_CLOSED: 401,

  INVALID_BET: 400,
  BET_BELOW_MIN: 400,
  BET_ABOVE_MAX: 400,
  INSUFFICIENT_FUNDS: 402,
  PLAYER_BLOCKED: 403,
  PLAYER_NOT_FOUND: 404,

  ROUND_IN_PROGRESS: 409,
  ROUND_NOT_FOUND: 404,
  ROUND_ALREADY_CLOSED: 409,
  ROUND_NOT_CANCELLABLE: 409,

  WALLET_UNAVAILABLE: 502,
  WALLET_REJECTED: 402,

  GAME_UNAVAILABLE: 503,
  INTERNAL_ERROR: 500,
};

export function httpStatusForErrorCode(code: ProviderErrorCode): number {
  return HTTP_STATUS[code] ?? 500;
}

/** Thrown anywhere in the request path; serialised by the global filter. */
export class ProviderError extends Error {
  readonly code: ProviderErrorCode;
  readonly details?: Record<string, unknown>;

  constructor(code: ProviderErrorCode, message?: string, details?: Record<string, unknown>) {
    super(message ?? code);
    this.name = 'ProviderError';
    this.code = code;
    this.details = details;
  }

  get httpStatus(): number {
    return httpStatusForErrorCode(this.code);
  }

  toResponse(requestId?: string): ErrorResponse {
    return {
      status: 'ERROR',
      code: this.code,
      message: this.message,
      ...(requestId ? { requestId } : {}),
      ...(this.details ? { details: this.details } : {}),
    };
  }
}
